/**
 * RENDER — SERVICES LIST — самый большой render-домен.
 *
 * Phase 5 modularization (Sprint 6, 2026-05-07).
 * Извлечено из public/js/app.js (DOMAIN: RENDER — SERVICES LIST).
 *
 *   - renderServices()              — главный рендер карточек/таблицы услуг
 *                                     с группировкой по дню/региону/подрядчику,
 *                                     с переключением view-mode
 *   - renderServicesFlat()          — flat-режим (без группировки)
 *   - renderServicesTable()         — табличный компактный режим
 *   - bindServiceEvents()           — event delegation для inline-edit, bulk-checkboxes,
 *                                     status icons (booking/payment), timing toggle
 *   - renderServicesDetailList()    — список услуг в правой панели (для печати)
 *
 * Зависимости (через `this.X`):
 *   - this.state.services / hotels / flights / otherServices
 *   - this.calculate*() (через prototype, из app/calculations.js)
 *   - this.escapeHtml() (через prototype, из app/usage-stats.js)
 *   - this.formatDate(), this.calculateActivityDate() (классовые)
 *   - this.markQuoteAsUnsaved(), this.scheduleUpdate(), this.saveToLocalStorage()
 *
 * @module app/render-services-list
 */

const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

/**
 * Прикрепляет render-services методы к prototype класса.
 * @param {Function} ClassRef - класс ProfessionalQuoteCalculator
 */
export function attachRenderServicesList(ClassRef) {
    Object.assign(ClassRef.prototype, {
        renderServices() {
            const container = document.getElementById('services-list');
            if (!container) return;

            // ===== VIEW MODE CHECK - Проверка режима отображения =====
            // Если выбран табличный режим - используем renderServicesTable()
            if (this.state.servicesViewMode === 'table') {
                this.renderServicesTable();
                return;
            }

            // ===== CARDS MODE - Режим карточек (по умолчанию) =====
            // Проверяем режим сортировки
            const sortBy = this.state.servicesSortBy || 'order';

            // Если сортировка НЕ по дням и НЕ по регионам, показываем услуги без группировки
            if (sortBy !== 'day' && sortBy !== 'region') {
                this.renderServicesFlat();
                return;
            }

            // Phase 8a: применяем quick-filter (по статусу бронирования/оплаты).
            const visibleServices = this.state.services.filter(s => this.passesQuickFilter(s));

            // Группируем услуги по дням
            const servicesByDay = {};
            visibleServices.forEach(service => {
                const day = service.day || 1;
                if (!servicesByDay[day]) {
                    servicesByDay[day] = [];
                }
                servicesByDay[day].push(service);
            });

            // Сортируем дни
            const sortedDays = Object.keys(servicesByDay).map(d => parseInt(d)).sort((a, b) => a - b);

            // Рендерим услуги по дням с разделителями
            const html = sortedDays.map(day => {
                const servicesInDay = servicesByDay[day];

                // Собираем уникальные регионы для этого дня
                const regions = [...new Set(servicesInDay.map(s => s.region || this.currentRegion))];
                const regionText = regions.join(', ');

                // Получаем дату для первой услуги дня (все услуги дня имеют одинаковую дату)
                const activityDate = this.calculateActivityDate(day);
                const dateText = activityDate ? ` (${activityDate})` : '';

                // Разделитель дня - современный минималистичный стиль
                const daySeparator = `
                    <div style="
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        margin: 24px 0 16px 0;
                    ">
                        <div style="
                            flex: 0 0 auto;
                            font-size: 13px;
                            font-weight: 600;
                            color: #0f172a;
                            letter-spacing: -0.01em;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                        ">${_t('app.services_list.day_prefix', 'Day')} ${day}${dateText}</div>
                        <div style="
                            flex: 1;
                            height: 1px;
                            background: linear-gradient(90deg, #e2e8f0 0%, transparent 100%);
                        "></div>
                        <div style="
                            flex: 0 0 auto;
                            font-size: 10px;
                            font-weight: 600;
                            color: #64748b;
                            background: #f8fafc;
                            padding: 3px 10px;
                            border-radius: 12px;
                            text-transform: uppercase;
                            letter-spacing: 0.05em;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                        ">${regionText}</div>
                    </div>
                `;

                // Услуги этого дня
                const servicesHtml = servicesInDay.map(service => {
                    const isSelected = this.state.selectedServices.has(service.id);
                    const isExcluded = service.excludeFromMarkup;
                    const isFullProfit = service.fullProfit;
                    const classes = [];
                    if (isSelected) classes.push('selected');
                    if (isExcluded) {
                        classes.push('excluded');
                    }
                    if (isFullProfit) {
                        classes.push('full-profit');
                    }

                    return `
                        <div class="service-card ${classes.join(' ')}" data-service-id="${service.id}">
                            <div class="service-header">
                                <div class="service-header-left">
                                    <input type="checkbox" class="checkbox service-checkbox" ${isSelected ? 'checked' : ''}
                                           data-service-id="${service.id}"
                                           title="${_t('app.services_list.select_bulk', 'Select for bulk operations')}">
                                    <div>
                                        <div class="editable" contenteditable="true" data-field="name" data-id="${service.id}" style="font-size: 14px; font-weight: 600; color: var(--gray-900); line-height: 1.3;">${service.name}</div>
                                        <div style="font-size: 11px; color: var(--gray-500); margin-top: 1px; display: flex; align-items: center; gap: 4px;">
                                            <i data-lucide="map-pin" style="width: 10px; height: 10px; display: inline; vertical-align: text-top;"></i>
                                            <select class="service-region-select" data-service-id="${service.id}" style="font-size: 11px; border: none; background: transparent; color: var(--gray-500); padding: 0; cursor: pointer;" title="${_t('app.services_list.change_region', 'Change region')}">
                                                ${this.regions.map(region => `<option value="${region}" ${service.region === region ? 'selected' : ''}>${region}</option>`).join('')}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div style="display: flex; align-items: center; gap: 8px;">
                                    <!-- Статусы бронирования и оплаты -->
                                    <div style="display: flex; align-items: center; gap: 6px;">
                                        <button
                                            class="status-icon-btn booking-status-btn"
                                            data-service-id="${service.id}"
                                            data-status="${service.done ? 'booked' : 'none'}"
                                            title="${_t('app.services_list.status_booking', 'Booking status')}: ${service.done ? _t('app.services_list.status_booked', 'Booked') : _t('app.services_list.status_not_booked', 'Not booked')}"
                                            style="border: none; background: none; cursor: pointer; padding: 4px; border-radius: 4px; transition: all 0.2s; color: ${service.done ? '#10b981' : '#9ca3af'};">
                                            <i data-lucide="${service.done ? 'check-circle' : 'circle'}" style="width: 18px; height: 18px;"></i>
                                        </button>
                                        <button
                                            class="status-icon-btn payment-status-btn"
                                            data-service-id="${service.id}"
                                            data-status="${service.paid ? 'paid' : 'none'}"
                                            title="${_t('app.services_list.status_payment', 'Payment status')}: ${service.paid ? _t('app.services_list.status_paid', 'Paid') : _t('app.services_list.status_not_paid', 'Unpaid')}"
                                            style="border: none; background: none; cursor: pointer; padding: 4px; border-radius: 4px; transition: all 0.2s; color: ${service.paid ? '#10b981' : '#9ca3af'};">
                                            <i data-lucide="${service.paid ? 'circle-dollar-sign' : 'dollar-sign'}" style="width: 18px; height: 18px;"></i>
                                        </button>
                                    </div>
                                    <button class="btn btn-sm duplicate-service-btn" data-service-id="${service.id}" title="${_t('app.services_list.duplicate_service', 'Duplicate service')}" style="padding: 4px 8px; font-size: 12px; background: transparent; color: #9ca3af; border: 1px solid #e5e7eb; margin-right: 4px; transition: all 0.2s;">
                                        <i data-lucide="copy" style="width: 14px; height: 14px;"></i>
                                    </button>
                                    <button class="btn btn-sm remove-service-btn" data-service-id="${service.id}" title="${_t('app.services_list.remove_service', 'Remove service')}">×</button>
                                </div>
                            </div>
                            <div class="service-content">
                                <div class="form-row" style="align-items: flex-start; margin-bottom: calc(var(--spacing) * 1);">
                                    <label style="padding-top: 3px; font-size: 10px; color: var(--gray-500); text-transform: uppercase; letter-spacing: 0.3px; width: 100px; flex-shrink: 0;">${_t('app.services_list.label_description', 'Description:')}</label>
                                    <div class="editable" contenteditable="true" data-field="description" data-id="${service.id}" style="flex: 1; min-width: 0; font-size: 12px; color: var(--gray-700); line-height: 1.5;">${service.description}</div>
                                </div>

                                <div class="form-row" style="align-items: flex-start; margin-bottom: calc(var(--spacing) * 1);">
                                    <label style="padding-top: 3px; font-size: 10px; color: var(--gray-500); text-transform: uppercase; letter-spacing: 0.3px; width: 100px; flex-shrink: 0;">${_t('app.services_list.label_contractor', 'Supplier:')}</label>
                                    <div class="editable" contenteditable="true" data-field="contractor" data-id="${service.id}" style="flex: 1; min-width: 0; font-size: 12px; color: var(--gray-700); line-height: 1.5;">${this.getSupplierName(service)}</div>
                                </div>

                                <div class="form-row" style="margin-bottom: calc(var(--spacing) * 1); flex-wrap: wrap; row-gap: calc(var(--spacing) * 0.75);">
                                    <div style="display: flex; align-items: center; gap: 6px;">
                                        <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.label_day', 'Day:')}</label>
                                        <input type="number" class="input service-field" data-field="day" data-service-id="${service.id}" value="${service.day}" min="1" style="width: 50px; font-size: 11px; padding: 4px 6px; text-align: center;">
                                    </div>

                                    <div style="display: flex; align-items: center; gap: 6px;">
                                        <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.label_price', 'Price:')}</label>
                                        <input type="number" class="input service-field" data-field="price" data-service-id="${service.id}" value="${service.price}" min="0" style="width: 80px; font-size: 11px; padding: 4px 6px; text-align: center;">
                                        ${this._currencySelectHtml(service, 'service')}
                                    </div>

                                    <div style="display: flex; align-items: center; gap: 6px;">
                                        <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.label_quantity', 'Qty:')}</label>
                                        <div class="qty-control">
                                            <button class="qty-btn qty-decrease" data-service-id="${service.id}">−</button>
                                            <input type="number" class="qty-input service-field" data-field="quantity" data-service-id="${service.id}" value="${service.quantity}" min="1">
                                            <button class="qty-btn qty-increase" data-service-id="${service.id}">+</button>
                                        </div>
                                    </div>

                                    <div style="display: flex; align-items: center; gap: 6px;">
                                        <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.label_markup', 'Markup:')}</label>
                                        <input type="number" class="input service-field" data-field="markup" data-service-id="${service.id}" value="${service.markup}" min="0" style="width: 50px; font-size: 11px; padding: 4px 6px; text-align: center;">
                                        <span style="font-size: 10px; color: var(--gray-500);">%</span>
                                    </div>
                                </div>

                                <!-- Время начала/окончания (под катом) -->
                                <div style="border-top: 1px solid #f3f4f6; padding-top: calc(var(--spacing) * 0.5);">
                                    <button
                                        class="btn btn-sm timing-toggle-btn"
                                        data-service-id="${service.id}"
                                        style="font-size: 9px; padding: 2px 6px; background: #f9fafb; color: #6b7280; border: none; width: 100%; text-align: left; display: flex; align-items: center; gap: 4px;"
                                        title="${_t('app.services_list.timing_show', 'Show start/end time')}">
                                        <span class="timing-toggle-icon" style="font-size: 8px;">▶</span>
                                        <span>${_t('app.services_list.timing_button', '⏰ Timing')}</span>
                                        ${service.startTime || service.endTime ? `<span style="font-size: 8px; color: #3b82f6;">●</span>` : ''}
                                    </button>
                                    <div class="timing-content" id="timing-${service.id}" style="max-height: 0; overflow: hidden; transition: max-height 0.3s ease;">
                                        <div style="padding: calc(var(--spacing) * 0.75) 0; display: flex; flex-wrap: wrap; gap: calc(var(--spacing) * 0.75);">
                                            <div style="display: flex; align-items: center; gap: 6px;">
                                                <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.timing_start', '⏰ Start:')}</label>
                                                <input type="time" class="input service-field" data-field="startTime" data-service-id="${service.id}" value="${service.startTime || ''}" style="width: 90px; font-size: 11px; padding: 4px 6px;">
                                            </div>

                                            <div style="display: flex; align-items: center; gap: 6px;">
                                                <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.timing_end', 'End:')}</label>
                                                <input type="time" class="input service-field" data-field="endTime" data-service-id="${service.id}" value="${service.endTime || ''}" style="width: 90px; font-size: 11px; padding: 4px 6px;">
                                            </div>

                                            <div style="display: flex; align-items: center; gap: 6px;">
                                                <span style="font-size: 10px; color: var(--gray-500);">${_t('app.services_list.timing_duration', 'Duration:')}</span>
                                                <span style="font-size: 11px; font-weight: 500; color: var(--gray-700);" id="duration-${service.id}">
                                                    ${this.calculateDuration(service.startTime, service.endTime)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="form-row" style="margin-bottom: 0; align-items: center; flex-wrap: wrap; row-gap: calc(var(--spacing) * 1);">
                                    <label style="display: flex; align-items: center; gap: 6px; font-size: 10px; color: var(--gray-600); white-space: nowrap;">
                                        <input type="checkbox" class="checkbox service-move-to-other-checkbox" data-service-id="${service.id}" title="${_t('app.services_list.move_to_other_title', 'Move to Other services section')}">
                                        <span>${_t('app.services_list.move_to_other', '📋 Other')}</span>
                                    </label>

                                    <label style="display: flex; align-items: center; gap: 6px; font-size: 10px; color: var(--gray-600); white-space: nowrap;">
                                        <input type="checkbox" class="checkbox service-exclude-checkbox" data-service-id="${service.id}" ${service.excludeFromMarkup ? 'checked' : ''} title="${_t('app.services_list.exclude_markup_title', 'Exclude markup for this service')}">
                                        <span>${_t('app.services_list.exclude_markup', 'Exclude markup')}</span>
                                    </label>

                                    <label style="display: flex; align-items: center; gap: 6px; font-size: 10px; color: var(--gray-600); white-space: nowrap;">
                                        <input type="checkbox" class="checkbox service-full-profit-checkbox" data-service-id="${service.id}" ${service.fullProfit ? 'checked' : ''} title="${_t('app.services_list.full_profit_title', 'Full amount goes to profit (cost = 0)')}">
                                        <span>${_t('app.services_list.full_profit', '💰 Full margin')}</span>
                                    </label>

                                    <div style="display: flex; align-items: center; gap: 6px;">
                                        <label style="font-size: 10px; color: var(--gray-600); white-space: nowrap;">${_t('app.services_list.prepayment_label', '💳 Prepayment:')}</label>
                                        <input type="number" class="input service-field" data-field="prepayment" data-service-id="${service.id}" value="${service.prepayment || 0}" min="0" step="0.01" style="width: 90px; font-size: 11px; padding: 4px 6px;" title="${_t('app.services_list.prepayment_title', 'Required prepayment amount')}">
                                    </div>

                                    <div style="display: flex; align-items: center; gap: 6px;">
                                        <label style="font-size: 10px; color: var(--gray-600); white-space: nowrap;">${_t('app.services_list.prepayment_deadline', '📅 Prepayment deadline:')}</label>
                                        <input type="date" class="input service-field" data-field="prepaymentDeadline" data-service-id="${service.id}" value="${service.prepaymentDeadline || ''}" style="width: 130px; font-size: 11px; padding: 4px 6px;" title="${_t('app.services_list.prepayment_deadline_title', 'Latest prepayment date')}">
                                    </div>

                                    <div style="margin-left: auto; text-align: right; min-width: 80px;">
                                        <div style="font-size: 15px; font-weight: 600; color: var(--success); white-space: nowrap;">
                                            ${this.formatCurrency(this.calculateServiceTotal(service))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    `;
                }).join('');

                return daySeparator + servicesHtml;
            }).join('');

            container.innerHTML = html;

            // Bind events with delegation
            this.bindServiceEvents();

            // Update bulk controls
            this.updateBulkControls();

            // Update filters visibility
            this.updateFiltersVisibility();

            // Debounced detail list rendering for better performance
            this.debouncedRenderDetail();

            // Инициализация Lucide иконок
            lucide.createIcons();
        },

        renderServicesFlat() {
            const container = document.getElementById('services-list');
            if (!container) return;

            // Phase 8a: применяем quick-filter (по статусу бронирования/оплаты).
            const visibleServices = this.state.services.filter(s => this.passesQuickFilter(s));

            container.innerHTML = visibleServices.map(service => {
                const isSelected = this.state.selectedServices.has(service.id);
                const isExcluded = service.excludeFromMarkup;
                const isFullProfit = service.fullProfit;
                const classes = [];
                if (isSelected) classes.push('selected');
                if (isExcluded) {
                    classes.push('excluded');
                }
                if (isFullProfit) {
                    classes.push('full-profit');
                }

                const activityDate = this.calculateActivityDate(service.day);

                return `
                    <div class="service-card ${classes.join(' ')}" data-service-id="${service.id}">
                        <div class="service-header">
                            <div class="service-header-left">
                                <input type="checkbox" class="checkbox service-checkbox" ${isSelected ? 'checked' : ''}
                                       data-service-id="${service.id}"
                                       title="${_t('app.services_list.select_bulk', 'Select for bulk operations')}">
                                <div>
                                    <div class="editable" contenteditable="true" data-field="name" data-id="${service.id}" style="font-size: 14px; font-weight: 600; color: var(--gray-900); line-height: 1.3;">${service.name}</div>
                                    <div style="font-size: 11px; color: var(--gray-500); margin-top: 1px; display: flex; align-items: center; gap: 4px;">
                                        ${_t('app.services_list.day_prefix', 'Day')} ${service.day}${activityDate ? ' (' + activityDate + ')' : ''} ·
                                        <i data-lucide="map-pin" style="width: 10px; height: 10px; display: inline; vertical-align: text-top;"></i>
                                        <select class="service-region-select" data-service-id="${service.id}" style="font-size: 11px; border: none; background: transparent; color: var(--gray-500); padding: 0; cursor: pointer;" title="${_t('app.services_list.change_region', 'Change region')}">
                                            ${this.regions.map(region => `<option value="${region}" ${service.region === region ? 'selected' : ''}>${region}</option>`).join('')}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div style="display: flex; align-items: center; gap: 8px;">
                                <!-- Phase 8a: Статусы бронирования и оплаты в flat-mode -->
                                <div style="display: flex; align-items: center; gap: 6px;">
                                    <button
                                        class="status-icon-btn booking-status-btn"
                                        data-service-id="${service.id}"
                                        data-status="${service.done ? 'booked' : 'none'}"
                                        title="${_t('app.services_list.status_booking', 'Booking status')}: ${service.done ? _t('app.services_list.status_booked', 'Booked') : _t('app.services_list.status_not_booked', 'Not booked')}"
                                        style="border: none; background: none; cursor: pointer; padding: 4px; border-radius: 4px; transition: all 0.2s; color: ${service.done ? '#10b981' : '#9ca3af'};">
                                        <i data-lucide="${service.done ? 'check-circle' : 'circle'}" style="width: 18px; height: 18px;"></i>
                                    </button>
                                    <button
                                        class="status-icon-btn payment-status-btn"
                                        data-service-id="${service.id}"
                                        data-status="${service.paid ? 'paid' : 'none'}"
                                        title="${_t('app.services_list.status_payment', 'Payment status')}: ${service.paid ? _t('app.services_list.status_paid', 'Paid') : _t('app.services_list.status_not_paid', 'Unpaid')}"
                                        style="border: none; background: none; cursor: pointer; padding: 4px; border-radius: 4px; transition: all 0.2s; color: ${service.paid ? '#10b981' : '#9ca3af'};">
                                        <i data-lucide="${service.paid ? 'circle-dollar-sign' : 'dollar-sign'}" style="width: 18px; height: 18px;"></i>
                                    </button>
                                </div>
                                <button class="btn btn-sm remove-service-btn" data-service-id="${service.id}" title="${_t('app.services_list.remove_service', 'Remove service')}">×</button>
                            </div>
                        </div>
                        <div class="service-content">
                            <div class="form-row" style="align-items: flex-start; margin-bottom: calc(var(--spacing) * 1);">
                                <label style="padding-top: 3px; font-size: 10px; color: var(--gray-500); text-transform: uppercase; letter-spacing: 0.3px; width: 100px; flex-shrink: 0;">${_t('app.services_list.label_description', 'Description:')}</label>
                                <div class="editable" contenteditable="true" data-field="description" data-id="${service.id}" style="flex: 1; min-width: 0; font-size: 12px; color: var(--gray-700); line-height: 1.5;">${service.description}</div>
                            </div>

                            <div class="form-row" style="align-items: flex-start; margin-bottom: calc(var(--spacing) * 1);">
                                <label style="padding-top: 3px; font-size: 10px; color: var(--gray-500); text-transform: uppercase; letter-spacing: 0.3px; width: 100px; flex-shrink: 0;">${_t('app.services_list.label_contractor', 'Supplier:')}</label>
                                <div class="editable" contenteditable="true" data-field="contractor" data-id="${service.id}" style="flex: 1; min-width: 0; font-size: 12px; color: var(--gray-700); line-height: 1.5;">${this.getSupplierName(service)}</div>
                            </div>

                            <div class="form-row" style="margin-bottom: calc(var(--spacing) * 1); flex-wrap: wrap; row-gap: calc(var(--spacing) * 0.75);">
                                <div style="display: flex; align-items: center; gap: 6px;">
                                    <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.label_day', 'Day:')}</label>
                                    <input type="number" class="input service-field" data-field="day" data-service-id="${service.id}" value="${service.day}" min="1" style="width: 50px; font-size: 11px; padding: 4px 6px; text-align: center;">
                                </div>

                                <div style="display: flex; align-items: center; gap: 6px;">
                                    <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.label_price', 'Price:')}</label>
                                    <input type="number" class="input service-field" data-field="price" data-service-id="${service.id}" value="${service.price}" min="0" style="width: 80px; font-size: 11px; padding: 4px 6px; text-align: center;">
                                    ${this._currencySelectHtml(service, 'service')}
                                </div>

                                <div style="display: flex; align-items: center; gap: 6px;">
                                    <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.label_quantity', 'Qty:')}</label>
                                    <div class="qty-control">
                                        <button class="qty-btn qty-decrease" data-service-id="${service.id}">−</button>
                                        <input type="number" class="qty-input service-field" data-field="quantity" data-service-id="${service.id}" value="${service.quantity}" min="1">
                                        <button class="qty-btn qty-increase" data-service-id="${service.id}">+</button>
                                    </div>
                                </div>

                                <div style="display: flex; align-items: center; gap: 6px;">
                                    <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.services_list.label_markup', 'Markup:')}</label>
                                    <input type="number" class="input service-field" data-field="markup" data-service-id="${service.id}" value="${service.markup}" min="0" style="width: 50px; font-size: 11px; padding: 4px 6px; text-align: center;">
                                    <span style="font-size: 10px; color: var(--gray-500);">%</span>
                                </div>
                            </div>

                            <div class="form-row" style="margin-bottom: 0; align-items: center; flex-wrap: wrap; row-gap: calc(var(--spacing) * 1);">
                                <label style="display: flex; align-items: center; gap: 6px; font-size: 10px; color: var(--gray-600); white-space: nowrap;">
                                    <input type="checkbox" class="checkbox service-exclude-checkbox" data-service-id="${service.id}" ${service.excludeFromMarkup ? 'checked' : ''} title="${_t('app.services_list.exclude_markup_title', 'Exclude markup for this service')}">
                                    <span>${_t('app.services_list.exclude_markup', 'Exclude markup')}</span>
                                </label>

                                <label style="display: flex; align-items: center; gap: 6px; font-size: 10px; color: var(--gray-600); white-space: nowrap;">
                                    <input type="checkbox" class="checkbox service-full-profit-checkbox" data-service-id="${service.id}" ${service.fullProfit ? 'checked' : ''} title="${_t('app.services_list.full_profit_title', 'Full amount goes to profit (cost = 0)')}">
                                    <span>${_t('app.services_list.full_profit', '💰 Full margin')}</span>
                                </label>

                                <div style="margin-left: auto; text-align: right; min-width: 80px;">
                                    <div style="font-size: 15px; font-weight: 600; color: var(--success); white-space: nowrap;">
                                        ${this.formatCurrency(this.calculateServiceTotal(service))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                `;
            }).join('');

            // Bind events with delegation
            this.bindServiceEvents();

            // Update bulk controls
            this.updateBulkControls();

            // Update filters visibility
            this.updateFiltersVisibility();

            // Debounced detail list rendering for better performance
            this.debouncedRenderDetail();

            // Инициализация Lucide иконок
            lucide.createIcons();
        },

        // ===== TABLE VIEW MODE - Табличный режим отображения =====
        // Компактный табличный вид для быстрого просмотра всех услуг без прокрутки
        renderServicesTable() {
            const container = document.getElementById('services-list');
            if (!container) return;

            if (this.state.services.length === 0) {
                container.innerHTML = `<div style="text-align: center; padding: 40px; color: #9ca3af;">${_t('app.services_list.no_services', 'No services added')}</div>`;
                return;
            }

            const sortBy = this.state.servicesSortBy || 'order';

            // Группируем услуги в зависимости от типа сортировки
            let groupedServices = {};
            let groupKey = '';

            if (sortBy === 'day') {
                // Группировка по дням
                this.state.services.forEach(service => {
                    const day = service.day || 1;
                    const activityDate = this.calculateActivityDate(day);
                    const key = `${_t('app.services_list.day_prefix', 'Day')} ${day}${activityDate ? ' (' + activityDate + ')' : ''}`;
                    if (!groupedServices[key]) {
                        groupedServices[key] = [];
                    }
                    groupedServices[key].push(service);
                });
            } else if (sortBy === 'region') {
                // Группировка по регионам (заглавными буквами)
                this.state.services.forEach(service => {
                    const region = (service.region || this.currentRegion || _t('app.services_list.no_region', 'No region')).toUpperCase();
                    const key = region;
                    if (!groupedServices[key]) {
                        groupedServices[key] = [];
                    }
                    groupedServices[key].push(service);
                });
            } else if (sortBy === 'contractor') {
                // Группировка по поставщикам
                this.state.services.forEach(service => {
                    // 🆕 Phase 9: используем supplier_id-cache, fallback на contractor строку
                    const contractor = this.getSupplierName(service) || _t('app.services_list.no_supplier', 'No supplier');
                    const key = `🏢 ${contractor}`;
                    if (!groupedServices[key]) {
                        groupedServices[key] = [];
                    }
                    groupedServices[key].push(service);
                });
            } else {
                // Без группировки (сортировка по порядку)
                groupedServices[_t('app.services_list.all_services', 'All services')] = this.state.services;
            }

            // Создаем HTML для каждой группы
            let tablesHTML = '';

            Object.keys(groupedServices).forEach(groupName => {
                const services = groupedServices[groupName];

                tablesHTML += `
                    <div style="margin-bottom: 32px;">
                        ${sortBy !== 'order' ? `
                            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
                                <div style="flex: 0 0 auto; font-size: 13px; font-weight: 600; color: #0f172a; letter-spacing: -0.01em; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">${groupName}</div>
                                <div style="flex: 1; height: 1px; background: linear-gradient(90deg, #e2e8f0 0%, transparent 100%);"></div>
                                <div style="flex: 0 0 auto; font-size: 11px; font-weight: 600; color: #64748b; background: #f8fafc; padding: 3px 10px; border-radius: 12px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">${services.length}</div>
                            </div>
                        ` : ''}

                        <table class="services-table-view">
                            <thead>
                                <tr>
                                    <th class="col-name">${_t('app.services_list.th_service', 'Service')}</th>
                                    <th class="col-contractor">${_t('app.services_list.th_contractor', 'Supplier')}</th>
                                    <th class="col-day">${_t('app.services_list.th_day', 'Day')}</th>
                                    <th class="col-time" style="font-size: 11px;">${_t('app.services_list.th_time', 'Time')}</th>
                                    <th class="col-qty">${_t('app.services_list.th_qty', 'Qty')}</th>
                                    <th class="col-total">${_t('app.services_list.th_total', 'Total')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${services.map(service => {
                                    const total = this.calculateServiceTotal(service);
                                    // 🆕 Group 3: тайминги в табличном режиме
                                    const hasTime = service.startTime || service.endTime;
                                    let timeDisplay = '—';
                                    if (service.startTime && service.endTime) {
                                        const dur = this.calculateDuration(service.startTime, service.endTime);
                                        timeDisplay = `<span style="color: #475569;">${service.startTime}–${service.endTime}</span>${dur && dur !== '—' ? ` <span style="color: #94a3b8; font-size: 9px;">(${dur})</span>` : ''}`;
                                    } else if (service.startTime) {
                                        timeDisplay = `<span style="color: #475569;">${_t('app.services_list.time_from', 'from {time}').replace('{time}', service.startTime)}</span>`;
                                    } else if (service.endTime) {
                                        timeDisplay = `<span style="color: #475569;">${_t('app.services_list.time_until', 'until {time}').replace('{time}', service.endTime)}</span>`;
                                    } else {
                                        timeDisplay = '<span style="color: #cbd5e1; font-size: 9px;">—</span>';
                                    }

                                    return `
                                        <tr data-service-id="${service.id}">
                                            <td class="col-name" title="${this.escapeHtml(service.description || '')}">
                                                ${this.escapeHtml(service.name)}
                                            </td>
                                            <td class="col-contractor" title="${this.escapeHtml(this.getSupplierName(service))}">
                                                <span style="color: #64748b; font-size: 10px;">${this.escapeHtml(this.getSupplierName(service) || '—')}</span>
                                            </td>
                                            <td class="col-day">
                                                <input type="number"
                                                       class="table-input service-field"
                                                       data-field="day"
                                                       data-service-id="${service.id}"
                                                       value="${service.day}"
                                                       min="1">
                                            </td>
                                            <td class="col-time" style="font-size: 10px; white-space: nowrap;">
                                                ${timeDisplay}
                                            </td>
                                            <td class="col-qty">
                                                ${service.quantity}
                                            </td>
                                            <td class="col-total">
                                                <strong>${this.formatCurrency(total)}</strong>
                                            </td>
                                        </tr>
                                    `;
                                }).join('')}
                            </tbody>
                        </table>
                    </div>
                `;
            });

            container.innerHTML = tablesHTML;

            // Bind events with delegation (только для редактирования дня)
            this.bindServiceEvents();

            // Update filters visibility
            this.updateFiltersVisibility();

            // Debounced detail list rendering for better performance
            this.debouncedRenderDetail();

            // Инициализация Lucide иконок
            lucide.createIcons();
        },

        // Improved event binding with delegation
        bindServiceEvents() {
            const container = document.getElementById('services-list');
            if (!container) return;

            // Remove old listeners to prevent duplicates
            if (this.handleServiceChange) {
                container.removeEventListener('change', this.handleServiceChange);
                container.removeEventListener('click', this.handleServiceClick);
            }

            // Bind new listeners
            this.handleServiceChange = (e) => {
                if (e.target.classList.contains('service-checkbox')) {
                    this.toggleServiceSelection(e.target.dataset.serviceId);
                } else if (e.target.classList.contains('service-region-select')) {
                    this.updateServiceRegion(e.target.dataset.serviceId, e.target.value);
                } else if (e.target.classList.contains('service-move-to-other-checkbox')) {
                    this.moveServiceToOther(e.target.dataset.serviceId, e.target.checked);
                } else if (e.target.classList.contains('service-exclude-checkbox')) {
                    this.toggleServiceExclusion(e.target.dataset.serviceId);
                } else if (e.target.classList.contains('service-full-profit-checkbox')) {
                    this.toggleServiceFullProfit(e.target.dataset.serviceId);
                } else if (e.target.classList.contains('service-field')) {
                    const field = e.target.dataset.field;
                    const serviceId = e.target.dataset.serviceId;
                    this.updateServiceField(serviceId, field, e.target.value);
                } else {
                    console.log('No matching class found for:', e.target.classList);
                }
            };

            this.handleServiceClick = (e) => {
                const target = e.target.closest('button');
                if (!target) return;

                if (target.classList.contains('remove-service-btn')) {
                    this.removeService(target.dataset.serviceId);
                } else if (target.classList.contains('qty-decrease')) {
                    this.changeQuantity(target.dataset.serviceId, -1);
                } else if (target.classList.contains('qty-increase')) {
                    this.changeQuantity(target.dataset.serviceId, 1);
                } else if (target.classList.contains('booking-status-btn')) {
                    this.toggleBookingStatus(target.dataset.serviceId);
                } else if (target.classList.contains('payment-status-btn')) {
                    this.togglePaymentStatus(target.dataset.serviceId);
                } else if (target.classList.contains('timing-toggle-btn')) {
                    this.toggleServiceTiming(target.dataset.serviceId);
                } else if (target.classList.contains('duplicate-service-btn')) {
                    this.duplicateService(target.dataset.serviceId);
                }
            };

            container.addEventListener('change', this.handleServiceChange);
            container.addEventListener('click', this.handleServiceClick);

            // Bind editable events
            container.querySelectorAll('.editable').forEach(el => {
                el.addEventListener('blur', (e) => this.handleEdit(e.target));
                el.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        e.target.blur();
                    }
                });
            });
        },

        renderServicesDetailList() {
            const container = document.getElementById('services-detail-list');

            if (this.state.services.length === 0) {
                container.innerHTML = `<div class="text-xs text-muted">${_t('app.services_list.no_services_added', 'No services added')}</div>`;
                return;
            }

            container.innerHTML = this.state.services.map(service => {
                const activityDate = this.calculateActivityDate(service.day);
                return `
                    <div class="service-detail-item">
                        <div class="service-detail-name">${service.name}</div>
                        <div class="service-detail-info">
                            <span>${_t('app.services_list.day_prefix', 'Day')} ${service.day}${activityDate ? ' (' + activityDate + ')' : ''}</span>
                            <span>×${service.quantity}</span>
                            <span>${this.formatCurrency(this.calculateServiceTotal(service))}</span>
                        </div>
                    </div>
                `;
            }).join('');
        },
    });
}
